<template>
  <div
    v-if="isLoading"
    class="d-flex justify-center align-center"
    style="height: 100vh;"
  >
    <v-progress-circular
      indeterminate
      color="#2661FF"
      size="80"
    ></v-progress-circular>
  </div>
  <div v-else>
    <v-form ref="form" v-model="valid" lazy-validation>
      <v-row class="d-flex ma-4">
        <!------------------------------------------>
        <!-- NAME ---------------------------------->
        <!------------------------------------------>
        <v-col cols="12">
          <span class="subtitle-2">{{ inputs.pricingName.title }}</span>
          <v-text-field
            v-model="localPricingModel.name"
            :placeholder="inputs.pricingName.placeholder"
            class="rounded-lg mt-1"
            outlined
            dense
            :rules="inputs.pricingName.rules"
            required
          >
            <v-tooltip slot="append" bottom>
              <template v-slot:activator="{ on }">
                <v-icon v-on="on" color="grey lighten-1" size="22">help</v-icon>
              </template>
              <span style="max-width: 200px;">
                {{ inputs.pricingName.tooltip }}
              </span>
            </v-tooltip>
          </v-text-field>
        </v-col>
        <!------------------------------------------>
        <!-- DESCRIPTION --------------------------->
        <!------------------------------------------>
        <v-col cols="12">
          <span class="subtitle-2">{{ inputs.pricingDescription.title }}</span>
          <v-text-field
            v-model="localPricingModel.description"
            :placeholder="inputs.pricingDescription.placeholder"
            class="rounded-lg mt-1"
            outlined
            dense
            :rules="inputs.pricingDescription.rules"
            required
          >
            <v-tooltip slot="append" bottom>
              <template v-slot:activator="{ on }">
                <v-icon v-on="on" color="grey lighten-1" size="22">help</v-icon>
              </template>
              <span style="max-width: 200px;">
                {{ inputs.pricingDescription.tooltip }}
              </span>
            </v-tooltip>
          </v-text-field>
        </v-col>
        <!------------------------------------------>
        <!-- STATUS -------------------------------->
        <!------------------------------------------>
        <v-col cols="12" md="5">
          <span class="subtitle-2"
            >{{ inputs.pricingStatus.title }}
            <v-tooltip slot="append" bottom>
              <template v-slot:activator="{ on }">
                <v-icon v-on="on" color="grey lighten-1" size="18">help</v-icon>
              </template>
              <span style="max-width: 200px;">
                {{ inputs.pricingStatus.tooltip }}
              </span>
            </v-tooltip></span
          >
          <v-select
            v-model="localPricingModel.status"
            :items="inputs.pricingStatus.items"
            :placeholder="inputs.pricingStatus.placeholder"
            item-text="title"
            item-value="value"
            class="rounded-lg mt-1"
            outlined
            dense
            :rules="inputs.pricingStatus.rules"
            required
          >
          </v-select>
        </v-col>
        <!------------------------------------------>
        <!-- FREQUENCY ----------------------------->
        <!------------------------------------------>
        <v-col cols="12" md="7">
          <span class="subtitle-2">
            {{ inputs.pricingFrequency.title }}
            <v-tooltip slot="append" bottom>
              <template v-slot:activator="{ on }">
                <v-icon v-on="on" color="grey lighten-1" size="18">help</v-icon>
              </template>
              <span style="max-width: 200px;">
                {{ inputs.pricingFrequency.tooltip }}
              </span>
            </v-tooltip>
          </span>
          <v-select
            v-model="localPricingModel.frequency"
            :items="inputs.pricingFrequency.items"
            :placeholder="inputs.pricingFrequency.placeholder"
            item-text="title"
            item-value="value"
            class="rounded-lg mt-1"
            outlined
            dense
            :disabled="hasSavedPlans"
            :rules="inputs.pricingFrequency.rules"
            required
          >
          </v-select>
          <span v-if="hasSavedPlans" class="caption" style="color: #F04438;">
            {{ inputs.pricingFrequency.error_message }}
          </span>
        </v-col>
        <!------------------------------------------>
        <!-- LIMITS TABLE -------------------------->
        <!------------------------------------------>
        <v-col cols="12" md="6">
          <div class="d-flex align-center">
            <span class="subtitle-2">
              {{ inputs.pricingLimitsTable.title }}
              <v-tooltip slot="append" bottom>
                <template v-slot:activator="{ on }">
                  <v-icon v-on="on" color="grey lighten-1" size="18"
                    >help</v-icon
                  >
                </template>
                <span style="max-width: 200px;">
                  {{ inputs.pricingLimitsTable.tooltip }}
                </span>
              </v-tooltip>
            </span>
            <v-switch
              v-model="localPricingModel.hasLimitsTable"
              color="#2661FF"
              inset
              class="ml-6"
              :disabled="savedPlans.length > 0 && pricingModel.hasLimitsTable"
            />
            <span v-if="hasSavedPlans" class="caption" style="color: #F04438;">
              {{ inputs.pricingLimitsTable.error_message }}
            </span>
          </div>
        </v-col>
        <!------------------------------------------>
        <!-- VERSION ------------------------------->
        <!------------------------------------------>
        <v-col cols="12" md="6">
          <span class="subtitle-2">
            {{ inputs.pricingVersion.title }}
            <v-tooltip slot="append" bottom>
              <template v-slot:activator="{ on }">
                <v-icon v-on="on" color="grey lighten-1" size="18">help</v-icon>
              </template>
              <span style="max-width: 200px;">
                {{ inputs.pricingVersion.tooltip }}
              </span>
            </v-tooltip>
          </span>
          <v-select
            v-model="localPricingModel.version"
            :items="inputs.pricingVersion.items"
            :placeholder="inputs.pricingVersion.placeholder"
            item-text="title"
            item-value="value"
            class="rounded-lg mt-1"
            outlined
            dense
            :disabled="hasSavedPlans"
            :rules="inputs.pricingVersion.rules"
            required
          />
        </v-col>
        <!------------------------------------------>
        <!-- CURRENCIES ---------------------------->
        <!------------------------------------------>
        <v-col cols="12">
          <span class="subtitle-2"
            >{{ inputs.pricingCurrencies.title }}
            <v-tooltip slot="append" bottom>
              <template v-slot:activator="{ on }">
                <v-icon v-on="on" color="grey lighten-1" size="18">help</v-icon>
              </template>
              <span style="max-width: 200px;">
                {{ inputs.pricingCurrencies.tooltip }}
              </span>
            </v-tooltip>
          </span>
          <v-row>
            <v-col cols="12">
              <v-autocomplete
                v-model="localPricingModel.currencies"
                :items="currencies"
                :placeholder="inputs.pricingCurrencies.placeholder"
                :disabled="hasSavedPlans"
                multiple
                item-text="name"
                item-value="id"
                class="rounded-lg mt-1"
                outlined
                dense
                chips
                :rules="inputs.pricingCurrencies.rules"
                required
              >
              </v-autocomplete>
              <span
                v-if="hasSavedPlans"
                class="caption"
                style="color: #F04438;"
                >{{ inputs.pricingCurrencies.error_message }}</span
              >
            </v-col>
          </v-row>
        </v-col>
        <!------------------------------------------>
        <!-- SUBMIT -------------------------------->
        <!------------------------------------------>
        <v-col cols="12" class="d-flex justify-end" style="gap: 12px;">
          <v-btn color="#757575" @click="resetForm" :disabled="hasSavedPlans">
            <span style="color: white;">{{ $t('actions.clean') }}</span>
            <v-icon color="white" class="ml-1">restart_alt</v-icon>
          </v-btn>
          <v-btn
            color="#2661FF"
            @click="submitForm"
            :disabled="!valid || isSubmitLoading"
          >
            <span style="color: white;">{{ $t('common.success') }}</span>
            <v-icon color="white" class="ml-1">east</v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </v-form>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
export default {
  name: 'PricingModelSettings',
  data() {
    return {
      step: 1,
      isLoading: false,
      isSubmitLoading: false,
      valid: false,
      inputs: {
        pricingName: {
          title: this.$t('pricing_model.create.step_1.name.title'),
          placeholder: this.$t('pricing_model.create.step_1.name.title'),
          tooltip: this.$t('pricing_model.create.step_1.name.tooltip'),
          rules: [v => !!v || this.$t('pricing_model.create.step_1.name.rule')]
        },
        pricingID: {
          title: this.$t('pricing_model.create.step_1.id.title'),
          placeholder: this.$t('pricing_model.create.step_1.id.title'),
          tooltip: this.$t('pricing_model.create.step_1.id.tooltip')
        },
        pricingDescription: {
          title: this.$t('pricing_model.create.step_1.description.title'),
          placeholder: this.$t('pricing_model.create.step_1.description.title'),
          tooltip: this.$t('pricing_model.create.step_1.description.tooltip'),
          rules: [
            v =>
              (!!v && v.length > 10) ||
              this.$t('pricing_model.create.step_1.description.rule')
          ]
        },
        pricingStatus: {
          title: this.$t('pricing_model.create.step_1.status.title'),
          placeholder: this.$t('pricing_model.create.step_1.status.title'),
          tooltip: this.$t('pricing_model.create.step_1.status.tooltip'),
          items: [
            {
              title: this.$t('pricing_model.create.step_1.status.active'),
              value: 1
            },
            {
              title: this.$t('pricing_model.create.step_1.status.disabled'),
              value: 2
            }
          ],
          rules: [
            v => !!v || this.$t('pricing_model.create.step_1.status.rule')
          ]
        },
        pricingFrequency: {
          title: this.$t('pricing_model.create.step_1.frequency.title'),
          placeholder: this.$t('pricing_model.create.step_1.frequency.title'),
          tooltip: this.$t('pricing_model.create.step_1.frequency.tooltip'),
          items: [
            {
              title: this.$t('pricing_model.create.step_1.frequency.monthly'),
              value: 1
            },
            {
              title: this.$t('pricing_model.create.step_1.frequency.yearly'),
              value: 2
            },
            {
              title: this.$t(
                'pricing_model.create.step_1.frequency.monthly_and_yearly'
              ),
              value: 3
            }
          ],
          rules: [
            v => !!v || this.$t('pricing_model.create.step_1.frequency.rule')
          ],
          error_message: this.$t('pricing_model.create.step_1.frequency.error')
        },
        pricingLimitsTable: {
          title: this.$t('pricing_model.create.step_1.limits_table.title'),
          tooltip: this.$t('pricing_model.create.step_1.limits_table.tooltip'),
          error_message: this.$t(
            'pricing_model.create.step_1.limits_table.error'
          )
        },
        pricingVersion: {
          title: this.$t('pricing_model.create.step_1.version.title'),
          placeholder: this.$t('pricing_model.create.step_1.version.title'),
          tooltip: this.$t('pricing_model.create.step_1.version.tooltip'),
          items: [
            { title: '1', value: 1 },
            { title: '2', value: 2 }
          ],
          rules: [
            v =>
              !!v ||
              this.$t('messages.validation_error.required', {
                field: this.$t('pricing_model.create.step_1.version.title')
              })
          ]
        },
        pricingCurrencies: {
          title: this.$t('pricing_model.create.step_1.currencies.title'),
          placeholder: this.$t(
            'pricing_model.create.step_1.currencies.placeholder'
          ),
          tooltip: this.$t('pricing_model.create.step_1.currencies.tooltip'),
          rules: [
            v =>
              (!!v && v.length > 0) ||
              this.$t('pricing_model.create.step_1.currencies.rule')
          ],
          error_message: this.$t('pricing_model.create.step_1.currencies.error')
        }
      },
      localPricingModel: {
        name: null,
        description: null,
        status: null,
        frequency: null,
        hasLimitsTable: false,
        version: null,
        currencies: []
      }
    }
  },
  async created() {
    await this.init()
  },
  computed: {
    ...mapState({
      currencies: ({ countries: { currencies } }) => currencies,
      pricingModel: ({ pricingModels: { pricingModel } }) => pricingModel,
      savedPlans: ({ pricingModels: { savedPlans } }) => savedPlans
    }),
    hasSavedPlans() {
      return this.savedPlans.length > 0
    }
  },
  methods: {
    ...mapActions({
      getCurrencies: 'countries/getCurrencies'
    }),
    async init() {
      try {
        this.isLoading = true
        await this.getCurrencies()

        // Validate and assign if pricingModel is not empty
        if (this.pricingModel && Object.keys(this.pricingModel).length > 0) {
          this.localPricingModel = { ...this.pricingModel }
        }
      } catch (error) {
      } finally {
        this.isLoading = false
      }
    },
    resetForm() {
      this.$refs.form.reset()
    },
    async validate() {
      await this.$refs.form.validate()
    },
    async submitForm() {
      await this.validate()
      if (!this.valid) {
        return
      }

      try {
        this.isSubmitLoading = true

        this.$emit('nextStep', {
          pricingModel: this.localPricingModel,
          step: this.step
        })
      } catch (error) {
      } finally {
        this.isSubmitLoading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped></style>
